import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { InvoicesApiAdapter } from '../adapters/InvoicesApiAdapter';
import { Invoice } from '../models/Invoices';

interface UseInvoicesProps {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const useInvoices = ({ searchFilter, search, sortBy = 'id', sortDirection = 'desc' }: UseInvoicesProps = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const invoicesAdapter = new InvoicesApiAdapter(httpClient);

  const { data, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['invoices', searchFilter, search, sortBy, sortDirection],
      queryFn: ({ pageParam = 1 }) =>
        invoicesAdapter.getInvoicesPaginated({
          searchFilter,
          search,
          sortBy,
          sortDirection,
          page: pageParam,
          limit: 25,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    });

  const invoices: Invoice[] = data?.pages.flatMap((page) => page.invoices) ?? [];

  const total = data?.pages[0]?.total ?? 0;

  return {
    invoices,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
